import {Call, Device} from '@twilio/voice-sdk';
import cx from 'classnames';
import {DOMNode, domToReact, HTMLReactParserOptions, Element as ParserElement} from 'html-react-parser';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    AudioStreamLinearBar, Button,
    ClientDynamicContent, Icon, Loader, NodeElement, notifier, OnlineStatusIcon
} from 'components';
import {DynamicComponentNames, SipUnits as Units, SIP_DEFAULT_TEMPLATE} from 'constants/client';
import {ClientContext} from 'context';
import {useClientSession} from 'hooks';
import {ClientDynamicLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale';
import {OnlineStatus} from 'types';
import {dynamicContentUtils} from 'utils';

import './styles.scss';
import {LeaveSessionButton} from '../LeaveSessionButton';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.SESSION_VOICE;

const FRAME_SIZES_REMOTE = {width: 112, height: 20};

const {isMatchElement, renderDefaultNode} = dynamicContentUtils;
const getOptions = (props: any): HTMLReactParserOptions => {
    const {
        assistantStatusLabel,
        startCallButtonLabel,
        leaveSessionLabel,
        isAnswered,
        isRinging,
        startCall,
        endLocalSession,
        onMuteToggle,
        isMuted,
        remoteAudioStream,
        startCallInProgress,
        localParticipant
    } = props;

    return {
        replace: (domNode: any) => {
            if (!domNode.attribs) {
                return null;
            }

            const node = domNode as ParserElement;

            if (isMatchElement(Units.Icon, node)) {
                return (
                    <NodeElement node={node}>
                        <div className={cx('icon-wrapper', {answered: isAnswered})}>
                            {isRinging && <Loader />}
                            <Icon name='chatbot' size='6xl' thickness='xs' color='default-dimmed' />
                        </div>
                    </NodeElement>);
            }
            if (isMatchElement(Units.RemoteStream, node)) {
                return (
                    <NodeElement show={isAnswered && remoteAudioStream} node={node}>
                        <AudioStreamLinearBar audioStream={remoteAudioStream} frameSizes={FRAME_SIZES_REMOTE} />
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.Description, node)) {
                return <NodeElement node={node}>{assistantStatusLabel}</NodeElement>;
            }
            if (isMatchElement(Units.StartSessionButtonDefault, node)) {
                return (
                    <NodeElement show={!isAnswered && !isRinging} node={node} onClick={startCall}>
                        <Button
                            color='success'
                            rounded
                            icon='phone-call'
                            animated={!(startCallInProgress || isAnswered || isRinging)}
                            disabled={startCallInProgress || isAnswered || isRinging}
                        >
                            <span className='label'>{startCallButtonLabel}</span>
                        </Button>
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.StartSessionButton, node)) {
                return (
                    <NodeElement
                        show={!isAnswered && !isRinging}
                        node={node}
                        disabled={startCallInProgress || isAnswered || isRinging}
                        onClick={startCall}
                    >
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.MuteButton, node)) {
                return (
                    <NodeElement
                        show={isAnswered || isRinging}
                        node={node}
                        data-microphone={isMuted ? 'off' : 'on'}
                        onClick={onMuteToggle}
                    >
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.MuteButtonDefault, node)) {
                return (
                    <NodeElement show={isAnswered || isRinging} node={node} onClick={onMuteToggle}>
                        <Icon button name={isMuted ? 'microphone-off' : 'microphone-on'} />
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.LeaveSessionButton, node)) {
                return (
                    <NodeElement node={node} show={isAnswered || isRinging} onClick={endLocalSession}>
                        {domToReact(node.children as DOMNode[])}
                    </NodeElement>
                );
            }
            if (isMatchElement(Units.LeaveSessionButtonLabel, node)) {
                return (
                    <NodeElement node={node} show={isAnswered || isRinging}>{leaveSessionLabel}</NodeElement>
                );
            }
            if (isMatchElement(Units.LeaveSessionButtonDefault, node)) {
                return (
                    <NodeElement show={isAnswered || isRinging} node={node}>
                        <LeaveSessionButton onClick={endLocalSession} />
                    </NodeElement>
                );
            }

            return renderDefaultNode(node, {role: localParticipant.role});
        }
    };
};

export const SipView: React.FC = () => {
    const {t} = useTranslation();
    const {image} = useClientSession();
    const {twilioDevice, setTwilioDevice, initTwilioDevice, endLocalSession} = useContext(ClientContext);

    const [call, setCall] = useState<Call | null>(null);
    const [startCallInProgress, setStartCallInProgress] = useState(false);
    const [callStatus, setCallStatus] = useState<Call.State>(null);
    const isRinging = useMemo(() => [Call.State.Connecting, Call.State.Pending, Call.State.Ringing].includes(callStatus), [callStatus]);
    const isAnswered = useMemo(() => [Call.State.Open].includes(callStatus), [callStatus]);
    const [isMuted, setIsMuted] = useState(false);

    const [remoteAudioStream, setRemoteAudioStream] = useState<MediaStream>(null);
    const [localAudioStream, setLocalAudioStream] = useState<MediaStream>(null);

    const assistantStatusLabel = useMemo(() => (
        <div className='description-content'>
            <OnlineStatusIcon size='md' status={OnlineStatus.Online} />
            <div className='description-text'>{t(T_KEYS.CLIENT.BOT_STATUS_LABEL_ONLINE)}</div>
        </div>
    ), [t]);
    const startCallButtonLabel = useMemo(() => t(T_KEYS.CLIENT.CALL_START), [t]);
    const leaveSessionLabel = useMemo(() => t(T_KEYS.CLIENT.CALL_END), [t]);

    const onMuteToggle = useCallback(() => {
        if (!call) {
            return;
        }

        const muteUpdated = !call.isMuted();
        call.mute(muteUpdated);
        setIsMuted(muteUpdated);
    }, [call]);

    const startCall = useCallback(() => {
        if (!twilioDevice) {
            return;
        }

        if (startCallInProgress) {
            return;
        }
        setStartCallInProgress(true);

        navigator.mediaDevices.getUserMedia({audio: true, video: false})
            .then((stream) => {
                setLocalAudioStream(stream);
                twilioDevice.connect().then((_call) => {
                    setCall(_call);
                    setCallStatus(_call?.status());
                    _call?.on('volume', (inputVolume) => {
                        if (inputVolume > 0) {
                            const remoteStream = _call?.getRemoteStream();
                            setRemoteAudioStream(remoteStream);
                        }
                    });
                }).catch((e) => {
                    // eslint-disable-next-line no-console
                    console.log(e);
                    notifier.error(e.message);
                    setCallStatus(Call.State.Closed);
                }).finally(() => {
                    setStartCallInProgress(false);
                });
            }).catch((e) => {
                // eslint-disable-next-line no-console
                console.log(e);
                setStartCallInProgress(false);
                notifier.error({title: t(T_KEYS.ERROR.NOT_ALLOWED)});
            });

    }, [twilioDevice, t, startCallInProgress]);

    const stopLocalTracks = useCallback(() => {
        if (localAudioStream) {
            localAudioStream.getTracks().forEach((track) => track.stop());
        }
    }, [localAudioStream]);

    useEffect(() => {
        if (call) {
            const updateCallStatus = (_call: Call) => {
                const status = _call?.status();
                setCallStatus(status);

                if (status === Call.State.Closed) {
                    stopLocalTracks();
                }
            };

            const onError = (e: any) => {
                setCallStatus(Call.State.Closed);
                // eslint-disable-next-line no-console
                console.log(e);
            };

            const updateRinging = () => {
                setCallStatus(Call.State.Ringing);
            };

            call.on('ringing', updateRinging);
            call.on('accept', updateCallStatus);
            call.on('reject', updateCallStatus);
            call.on('disconnect', updateCallStatus);
            call.on('error', onError);
            call.on('reconnecting', updateCallStatus);
            call.on('reconnected', updateCallStatus);

            return () => {
                call.off('ringing', updateRinging);
                call.off('accept', updateCallStatus);
                call.off('reject', updateCallStatus);
                call.off('disconnect', updateCallStatus);
                call.off('error', onError);
                call.off('reconnecting', updateCallStatus);
                call.off('reconnected', updateCallStatus);
            };
        }
    }, [call, stopLocalTracks]);

    const handleEndLocalSession = useCallback(() => {
        if (!twilioDevice) {
            twilioDevice.disconnectAll();
            setCall(null);
            setCallStatus(null);
        }
        stopLocalTracks();
        endLocalSession();
    }, [twilioDevice, stopLocalTracks, endLocalSession]);

    useEffect(() => {
        if (Device.isSupported) {
            if (!twilioDevice) {
                initTwilioDevice();
            }
        } else {
            notifier.error({title: t(T_KEYS.ERROR.NOT_SUPPORTED)});
        }
    }, [twilioDevice, initTwilioDevice, t]);

    useEffect(() => () => {
        if (twilioDevice) {
            twilioDevice.disconnectAll();
            twilioDevice.destroy();
            setTwilioDevice(null);
            setCall(null);
            setCallStatus(null);
        }
    }, [twilioDevice, setTwilioDevice]);

    return (
        <ClientDynamicLayout image={image}>
            <ClientDynamicContent
                component={DynamicComponentNames.SIP}
                defaultTemplate={SIP_DEFAULT_TEMPLATE}
                optionsProps={{
                    assistantStatusLabel,
                    startCallButtonLabel,
                    leaveSessionLabel,
                    isAnswered,
                    isRinging,
                    isMuted,
                    remoteAudioStream,
                    localAudioStream,
                    startCallInProgress,
                    startCall,
                    onMuteToggle,
                    endLocalSession: handleEndLocalSession,
                }}
                optionsGetter={getOptions}
            />
        </ClientDynamicLayout>
    );
};